import { filterRowsByTeam } from '../../detectTeamRows';
import normalizeOcrText from '../../normalizeOcrText';
import { getControlLayout } from './layouts';

const FIELD_OCR_CONFIG = {
  player: {
    psm: '7',
    whitelist: '',
  },
  score: {
    psm: '7',
    whitelist: '0123456789',
  },
  kda: {
    psm: '7',
    whitelist: '0123456789/',
  },
  impact: {
    psm: '7',
    whitelist: '0123456789',
  },
};

const setWorkerParameters = async (worker, fieldKey) => {
  const config = FIELD_OCR_CONFIG[fieldKey] || FIELD_OCR_CONFIG.player;
  await worker.setParameters({
    tessedit_pageseg_mode: config.psm,
    tessedit_char_whitelist: config.whitelist,
    preserve_interword_spaces: '1',
  });
};

const cropCanvas = ({ canvas, row, fieldBounds, padding = 2 }) => {
  const sourceX = row.bounds.x + Math.round(row.bounds.width * fieldBounds.xStart) - padding;
  const sourceY = row.bounds.y + Math.round(row.bounds.height * fieldBounds.yStart) - padding;
  const sourceWidth = Math.round(row.bounds.width * (fieldBounds.xEnd - fieldBounds.xStart)) + (padding * 2);
  const sourceHeight = Math.round(row.bounds.height * (fieldBounds.yEnd - fieldBounds.yStart)) + (padding * 2);
  const sx = Math.max(0, sourceX);
  const sy = Math.max(0, sourceY);
  const sw = Math.min(canvas.width - sx, sourceWidth);
  const sh = Math.min(canvas.height - sy, sourceHeight);
  const output = document.createElement('canvas');
  const context = output.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Could not create field crop canvas.');
  }

  output.width = Math.max(1, sw);
  output.height = Math.max(1, sh);
  context.drawImage(canvas, sx, sy, sw, sh, 0, 0, output.width, output.height);

  return {
    canvas: output,
    bounds: { x: sx, y: sy, width: output.width, height: output.height },
    previewUrl: output.toDataURL('image/png'),
  };
};

const recognizeField = async ({ worker, canvas, fieldKey }) => {
  await setWorkerParameters(worker, fieldKey);
  const result = await worker.recognize(canvas, {}, { text: true });
  const data = result?.data || {};

  return {
    raw: normalizeOcrText(data.text || ''),
    confidence: typeof data.confidence === 'number' ? data.confidence : null,
  };
};

const parseNumber = (raw, fieldLabel, warnings) => {
  const compact = String(raw || '').replace(/[^\d]/g, '');
  if (!compact) {
    warnings.push(`${fieldLabel} failed from "${raw || ''}".`);
    return null;
  }

  const value = Number.parseInt(compact, 10);
  if (!Number.isInteger(value) || value < 0) {
    warnings.push(`${fieldLabel} produced invalid value "${raw || ''}".`);
    return null;
  }

  return value;
};

const parseKda = (raw, warnings) => {
  const compact = String(raw || '').replace(/\s+/g, '').replace(/[^0-9/]/g, '');
  const parts = compact.split('/').filter((part) => part !== '');

  if (parts.length !== 3) {
    warnings.push(`K/D/A failed from "${raw || ''}".`);
    return { kills: null, deaths: null, assists: null };
  }

  const values = parts.map((part) => Number.parseInt(part, 10));
  if (values.some((value) => !Number.isInteger(value) || value < 0)) {
    warnings.push(`K/D/A produced invalid value "${raw || ''}".`);
    return { kills: null, deaths: null, assists: null };
  }

  return {
    kills: values[0],
    deaths: values[1],
    assists: values[2],
  };
};

const getName = (raw, warnings) => {
  const detectedName = normalizeOcrText(raw).replace(/\n+/g, ' ').trim();
  if (!detectedName) {
    warnings.push('Player name was empty.');
  }
  return detectedName;
};

const parseMatchDetails = async ({
  worker,
  processedImage,
  detection,
  selectedTeamColor = 'blue',
}) => {
  if (!worker) {
    throw new Error('OCR worker is not ready.');
  }

  if (!processedImage?.canvas) {
    throw new Error('Processed Match Details image is required.');
  }

  const startedAt = performance.now();
  const layout = getControlLayout('matchDetails');
  const selectedRows = filterRowsByTeam(detection?.rows || [], selectedTeamColor)
    .sort((a, b) => (a.bounds.y - b.bounds.y) || (a.bounds.x - b.bounds.x));
  const parseWarnings = [];

  if (selectedRows.length !== 5) {
    parseWarnings.push(`Expected 5 ${selectedTeamColor.toUpperCase()} Match Details rows. Detected ${selectedRows.length}.`);
  }

  const records = [];
  let fieldCropCount = 0;

  for (const row of selectedRows) {
    const warnings = [];
    const crops = {};
    const fieldResults = {};

    for (const fieldKey of ['player', 'score', 'kda', 'impact']) {
      const crop = cropCanvas({
        canvas: processedImage.canvas,
        row,
        fieldBounds: layout.fields[fieldKey],
      });
      fieldCropCount += 1;
      crops[fieldKey] = crop;
      fieldResults[fieldKey] = await recognizeField({
        worker,
        canvas: crop.canvas,
        fieldKey,
      });
    }

    const kda = parseKda(fieldResults.kda.raw, warnings);

    records.push({
      sourceRowId: row.id,
      sourceRowIndex: row.rowIndex,
      detectedName: getName(fieldResults.player.raw, warnings),
      score: parseNumber(fieldResults.score.raw, 'Score', warnings),
      kills: kda.kills,
      deaths: kda.deaths,
      assists: kda.assists,
      impact: parseNumber(fieldResults.impact.raw, 'Impact', warnings),
      _ocr: fieldResults,
      _warnings: warnings,
      _crops: Object.fromEntries(Object.entries(crops).map(([key, crop]) => ([
        key,
        {
          bounds: crop.bounds,
          previewUrl: crop.previewUrl,
        },
      ]))),
    });
  }

  return {
    category: 'matchDetails',
    selectedTeamColor,
    selectedRowCount: selectedRows.length,
    fieldCropCount,
    elapsedMs: Math.round(performance.now() - startedAt),
    warnings: parseWarnings,
    records,
  };
};

export default parseMatchDetails;
